<template>
  <div class="container">
    <b-modal
      :model-value="modCatalogoIconos"
      :no-close-on-backdrop="true"
      hide-header
      hide-footer
      centered
      size="xl"
     @hide="cierraModal">
      <div class="text-center">
        <h4 class="mb-2">{{ labels.title }}</h4>
      </div>

      <div class="d-flex align-items-center gap-2 mb-3">
        <b-form-input
          v-model="searchText"
          :placeholder="labels.searchPlaceholder"
          class="catalog-search"
        />
        <b-button
          size="sm"
          :variant="showAllIcons ? 'outline-secondary' : 'secondary'"
          @click="showAllIcons = false"
        >
          {{ labels.commonMode }}
        </b-button>
        <b-button
          size="sm"
          :variant="showAllIcons ? 'secondary' : 'outline-secondary'"
          @click="showAllIcons = true"
        >
          {{ labels.allMode }}
        </b-button>
      </div>

      <div class="icon-grid">
        <div v-for="iconName in filteredIcons" :key="iconName" class="icon-card">
          <i :class="`fa-solid fa-${iconName}`"></i>
          <span>{{ iconName }}</span>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <small>{{ labels.total }}: <b>{{ filteredIcons.length }}</b></small>
        <b-button size="sm" variant="outline-secondary" @click="downloadIconCatalog">
          {{ labels.downloadCatalog }}
        </b-button>
      </div>

      <div class="mt-3 text-center">
        <b-button class="md-accent" @click="cierraModal">{{ labels.close }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { getMenuIconNames, buildMenuIconCatalogCsv } from '../../../utils/menuIconCatalog';

const ICON_KEYWORDS = {
  gear: ["engranaje", "configuracion", "configuración", "ajustes", "settings", "setup"],
  gears: ["engranajes", "configuracion", "configuración", "ajustes", "settings", "setup"],
  cog: ["engranaje", "configuracion", "configuración", "ajustes", "settings", "setup"],
  cogs: ["engranajes", "configuracion", "configuración", "ajustes", "settings", "setup"],
  sliders: ["filtros", "controles", "ajustes", "configuracion", "configuración"],
  wrench: ["llave", "herramienta", "tool", "reparar", "mantenimiento"],
  screwdriver: ["destornillador", "herramienta", "tool", "mantenimiento"],
  user: ["usuario", "perfil", "persona", "cuenta", "account"],
  users: ["usuarios", "personas", "equipo", "grupo", "team"],
  userGroup: ["usuarios", "personas", "equipo", "grupo", "team"],
  lock: ["candado", "seguridad", "password", "clave", "acceso"],
  unlock: ["desbloquear", "acceso", "seguridad", "abrir"],
  home: ["inicio", "casa", "principal", "dashboard"],
  house: ["inicio", "casa", "principal", "dashboard"],
  building: ["edificio", "empresa", "oficina", "compania", "compañia"],
  file: ["archivo", "documento", "doc"],
  folder: ["carpeta", "directorio", "files"],
  chartLine: ["grafica", "gráfica", "linea", "línea", "estadistica", "estadística"],
  chartPie: ["grafica", "gráfica", "pie", "pastel", "estadistica", "estadística", "reporte"],
  chartBar: ["grafica", "gráfica", "barras", "estadistica", "estadística", "reporte"],
  table: ["tabla", "datos", "grid"],
  list: ["lista", "items", "ítems", "menu", "menú"],
  clipboard: ["portapapeles", "tareas", "checklist"],
  calendar: ["calendario", "fecha", "agenda"],
  bell: ["notificacion", "notificación", "alerta"],
  envelope: ["correo", "email", "mensaje"],
  paperPlane: ["enviar", "envio", "envío", "mensaje"],
  download: ["descargar", "bajar", "importar"],
  upload: ["subir", "cargar", "importar"],
  search: ["buscar", "lupa", "consulta"],
  magnifyingGlass: ["buscar", "lupa", "consulta"],
  filter: ["filtro", "buscar", "refinar"],
  eye: ["ver", "visualizar", "detalle"],
  eyeSlash: ["ocultar", "esconder", "no ver"],
  trash: ["eliminar", "borrar", "papelera"],
  trashCan: ["eliminar", "borrar", "papelera"],
  pen: ["editar", "lapiz", "lápiz", "modificar"],
  pencil: ["editar", "lapiz", "lápiz", "modificar"],
  plus: ["agregar", "crear", "nuevo", "sumar"],
  minus: ["restar", "quitar", "eliminar"],
  check: ["ok", "aceptar", "confirmar", "aprobado"],
  xmark: ["cerrar", "cancelar", "error", "eliminar"],
  circleInfo: ["info", "informacion", "información", "ayuda"],
  circleQuestion: ["ayuda", "pregunta", "faq"],
  fileInvoice: ["factura", "invoice", "cuenta", "cobro"],
  fileSignature: ["firma", "contrato", "documento"],
  moneyBill: ["dinero", "pago", "efectivo"],
  moneyBillWave: ["dinero", "pago", "efectivo"],
  sackDollar: ["dinero", "bolsa", "presupuesto"],
};

const COMMON_ICON_NAMES = [
  "cog", "cogs", "gear", "gears", "sliders", "wrench", "screwdriver", "toolbox", "hammer",
  "house", "home", "building", "city", "warehouse", "store",
  "user", "users", "user-group", "user-gear", "user-plus", "id-card",
  "lock", "unlock", "key", "shield", "fingerprint", "user-shield",
  "list", "list-ul", "table", "bars", "grip", "th-large",
  "folder", "folder-open", "file", "file-lines", "file-pen", "file-export", "file-import",
  "clipboard", "clipboard-check", "clipboard-list", "copy", "paste",
  "calendar", "calendar-check", "clock", "hourglass", "stopwatch",
  "bell", "envelope", "message", "comments", "phone",
  "magnifying-glass", "filter", "eye", "eye-slash", "circle-info", "circle-question",
  "chart-line", "chart-pie", "chart-column", "chart-bar", "arrow-trend-up", "signal",
  "money-bill", "money-bill-wave", "sack-dollar", "wallet", "coins", "credit-card",
  "cart-shopping", "receipt", "file-invoice", "file-invoice-dollar", "cash-register",
  "plus", "minus", "xmark", "check", "pen", "pencil", "trash", "trash-can", "download", "upload",
  "arrow-up", "arrow-down", "arrow-left", "arrow-right", "rotate", "rotate-left", "rotate-right",
  "floppy-disk", "database", "server", "globe", "link", "paper-plane",
  "book", "book-open", "graduation-cap", "lightbulb", "star", "flag"
];

const normalizeText = (text) =>
  (text || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const kebabToCamel = (value) =>
  value.replace(/-([a-z])/g, (_, char) => char.toUpperCase());

const TOKEN_ALIASES = {
  box: ["caja", "cajas", "contenedor", "contenedores", "paquete", "paquetes"],
  boxes: ["caja", "cajas", "contenedor", "contenedores", "paquete", "paquetes"],
  cube: ["cubo", "cubos", "caja", "cajas"],
  cubes: ["cubo", "cubos", "caja", "cajas"],
  archive: ["archivo", "archivar", "historial"],
  file: ["archivo", "documento", "fichero"],
  files: ["archivos", "documentos", "ficheros"],
  folder: ["carpeta", "directorio"],
  open: ["abrir", "abierto", "abierta"],
  close: ["cerrar", "cerrado", "cerrada"],
  user: ["usuario", "usuarios", "persona", "personas", "perfil"],
  users: ["usuario", "usuarios", "persona", "personas", "perfiles"],
  group: ["grupo", "equipos", "equipo"],
  lock: ["candado", "bloqueo", "seguridad"],
  unlock: ["desbloquear", "acceso", "seguridad"],
  key: ["llave", "clave", "acceso"],
  search: ["buscar", "busqueda", "búsqueda", "lupa", "consulta"],
  magnifying: ["lupa", "buscar", "busqueda", "búsqueda"],
  chart: ["grafica", "gráfica", "estadistica", "estadística", "indicador", "indicadores", "reporte"],
  line: ["linea", "línea"],
  pie: ["pastel", "torta", "circular"],
  bar: ["barra", "barras"],
  table: ["tabla", "tablas", "datos"],
  list: ["lista", "listado", "menu", "menú"],
  calendar: ["calendario", "fecha", "agenda"],
  clock: ["reloj", "tiempo", "hora"],
  bell: ["campana", "campaña", "notificacion", "notificación", "alerta"],
  envelope: ["correo", "email", "mensaje"],
  message: ["mensaje", "mensajes", "chat"],
  plus: ["agregar", "sumar", "crear", "nuevo", "añadir", "adicionar"],
  minus: ["restar", "quitar", "remover"],
  check: ["confirmar", "aceptar", "aprobado", "ok", "validar"],
  xmark: ["cerrar", "cancelar", "eliminar", "error"],
  trash: ["basura", "papelera", "eliminar", "borrar"],
  pencil: ["lapiz", "lápiz", "editar", "edicion", "edición", "modificar"],
  pen: ["lapiz", "lápiz", "editar", "edicion", "edición", "modificar"],
  gear: ["engranaje", "configuracion", "configuración", "ajustes", "parametros", "parametrizacion"],
  cog: ["engranaje", "configuracion", "configuración", "ajustes", "parametros", "parametrizacion"],
  money: ["dinero", "plata", "pago", "pagos"],
  dollar: ["dolar", "dólar", "dinero", "plata"],
  wallet: ["billetera", "cartera", "dinero"],
  coin: ["moneda", "monedas"],
  coins: ["moneda", "monedas"],
  credit: ["credito", "crédito", "tarjeta"],
  card: ["tarjeta", "credencial"],
  download: ["descargar", "bajar", "exportar"],
  upload: ["subir", "cargar", "importar"],
  arrow: ["flecha", "flechas", "direccion", "dirección"],
  left: ["izquierda"],
  right: ["derecha"],
  up: ["arriba", "subir"],
  down: ["abajo", "bajar"],
  home: ["inicio", "principal", "casa"],
  house: ["inicio", "principal", "casa"],
  building: ["edificio", "empresa", "oficina"],
  city: ["ciudad"],
  server: ["servidor", "backend"],
  database: ["base", "datos", "bd", "base de datos"],
  globe: ["mundo", "global", "web", "internet"],
  eye: ["ver", "visualizar", "detalle"],
  filter: ["filtro", "filtrar", "refinar"],
  info: ["informacion", "información", "ayuda"],
  question: ["pregunta", "ayuda", "faq"],
  signature: ["firma", "firmar", "contrato"],
  invoice: ["factura", "cobro"],
  receipt: ["recibo", "comprobante"],
  clipboard: ["portapapeles", "tareas", "checklist"],
  paper: ["papel", "documento"],
  plane: ["enviar", "envio", "envío"],
  book: ["libro", "manual", "guia", "guía"],
  star: ["estrella", "favorito", "destacado"],
  flag: ["bandera", "marcar", "señalar"],
  phone: ["telefono", "teléfono", "llamada"],
  comments: ["comentarios", "mensajes", "chat"],
  comment: ["comentario", "mensaje", "chat"],
  shield: ["escudo", "seguridad", "proteccion", "protección"],
  fingerprint: ["huella", "biometria", "biometría"],
  id: ["identificacion", "identificación", "documento"],
  badge: ["credencial", "identificacion", "identificación"],
  briefcase: ["maletin", "maletín", "trabajo", "negocio"],
  cart: ["carrito", "compras", "tienda"],
  shop: ["tienda", "compras"],
  store: ["tienda", "almacen", "almacén"],
  truck: ["camion", "camión", "envio", "envío", "transporte"],
  route: ["ruta", "camino"],
  map: ["mapa", "ubicacion", "ubicación"],
  location: ["ubicacion", "ubicación", "lugar"],
  pin: ["pin", "marcador", "ubicacion", "ubicación"],
  wifi: ["internet", "red", "conexion", "conexión"],
  cloud: ["nube", "internet"],
  bolt: ["rayo", "energia", "energía", "rapido", "rápido"],
  fire: ["fuego", "caliente"],
  bug: ["error", "bug", "fallo"],
  code: ["codigo", "código", "desarrollo"],
  terminal: ["terminal", "consola"],
  image: ["imagen", "foto", "fotos"],
  camera: ["camara", "cámara", "foto"],
  video: ["video", "vídeo"],
  music: ["musica", "música", "audio"],
  play: ["reproducir", "play", "iniciar"],
  pause: ["pausa", "detener"],
  stop: ["detener", "parar"],
};

const getWordVariants = (word) => {
  const term = normalizeText(word);
  if (!term) return [];
  const variants = new Set([term]);

  if (term.endsWith("ies") && term.length > 3) variants.add(`${term.slice(0, -3)}y`);
  if (term.endsWith("es") && term.length > 4) variants.add(term.slice(0, -2));
  if (term.endsWith("s") && term.length > 3) variants.add(term.slice(0, -1));
  if (!term.endsWith("s")) variants.add(`${term}s`);
  if (!term.endsWith("es")) variants.add(`${term}es`);

  if (term.endsWith("cion")) variants.add(`${term.slice(0, -4)}cion`);
  if (term.endsWith("ciones")) variants.add(`${term.slice(0, -2)}`);

  return Array.from(variants);
};

const REVERSE_ALIAS_MAP = Object.entries(TOKEN_ALIASES).reduce((acc, [token, aliases]) => {
  const key = normalizeText(token);
  const allValues = [key, ...aliases.map(normalizeText)];
  allValues.forEach((value) => {
    if (!acc[value]) acc[value] = new Set();
    acc[value].add(key);
  });
  return acc;
}, {});

const expandQueryTerms = (term) => {
  const base = getWordVariants(term);
  const expanded = new Set(base);

  base.forEach((item) => {
    const mapped = REVERSE_ALIAS_MAP[item];
    if (mapped) {
      mapped.forEach((token) => {
        getWordVariants(token).forEach((v) => expanded.add(v));
        (TOKEN_ALIASES[token] || []).map(normalizeText).forEach((v) => expanded.add(v));
      });
    }
  });

  return Array.from(expanded);
};

const getSearchKeywordsFromTokens = (iconName) => {
  const tokens = normalizeText(iconName).split("-").filter(Boolean);
  const expanded = new Set(tokens);

  tokens.forEach((token) => {
    getWordVariants(token).forEach((v) => expanded.add(v));
    (TOKEN_ALIASES[token] || []).map(normalizeText).forEach((alias) => {
      expanded.add(alias);
      getWordVariants(alias).forEach((v) => expanded.add(v));
    });
  });

  return Array.from(expanded);
};

export default {
  name: "CatalogoIconos",
  props: ['modCatalogoIconos'],
  data: () => ({
    iconOptions: [],
    searchText: "",
    showAllIcons: false
  }),
  computed: {
    labels() {
      if (this.$store.state._language == "es-ES") {
        return {
          title: "Catálogo de iconos",
          close: "Cerrar",
          downloadCatalog: "Descargar catálogo",
          total: "Resultados",
          searchPlaceholder: "Buscar por nombre o palabra clave (ej: engranaje, usuario, reporte)",
          commonMode: "Más usados",
          allMode: "Ver todos"
        };
      }
      return {
        title: "Icon catalog",
        close: "Close",
        downloadCatalog: "Download catalog",
        total: "Results",
        searchPlaceholder: "Search by name or keyword (e.g. gear, user, report)",
        commonMode: "Most used",
        allMode: "Show all"
      };
    },
    commonIcons() {
      const allowed = new Set(this.iconOptions);
      return COMMON_ICON_NAMES.filter((name) => allowed.has(name));
    },
    iconsInScope() {
      const term = normalizeText(this.searchText);
      if (term) return this.iconOptions;
      return this.showAllIcons ? this.iconOptions : this.commonIcons;
    },
    filteredIcons() {
      const term = normalizeText(this.searchText);
      if (!term) return this.iconsInScope;
      const queryTerms = expandQueryTerms(term);

      return this.iconsInScope.filter((name) => {
        const normalizedName = normalizeText(name);
        if (queryTerms.some((query) => normalizedName.includes(query) || query.includes(normalizedName))) return true;

        const camelName = kebabToCamel(name);
        const directKeywords = ICON_KEYWORDS[camelName] || [];
        const genericKeywords = getSearchKeywordsFromTokens(name);
        const keywords = [...directKeywords, ...genericKeywords].map(normalizeText);

        return keywords.some((keyword) =>
          queryTerms.some((query) => keyword.includes(query) || query.includes(keyword))
        );
      });
    }
  },
  mounted() {
    this.iconOptions = getMenuIconNames();
  },
  methods: {
    cierraModal() {
      this.$emit('closeModal', false);
    },
    downloadIconCatalog() {
      const csv = buildMenuIconCatalogCsv();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "catalogo-iconos-menu.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.catalog-search {
  max-width: 420px;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 4px;
}

.icon-card {
  border: 1px solid #d9dee8;
  border-radius: 8px;
  min-height: 68px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px;
  background: #fbfcff;
}

.icon-card i {
  width: 18px;
  text-align: center;
}

.icon-card span {
  font-size: 0.9rem;
}
</style>
